import React, { useState } from "react";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { timeago, folksongTempos, getSelectValue, exerciseTypes, christmasId } from "../../../../common/parameters";
import { formatComposerName } from "../../../../common/helpers/formatComposerName";
import { PublishButton } from "../../../../common/styles";
import { IStateTree } from "../../../store/interfaces";
import { IUser } from "../../../store/user/models";
import { host } from "../../../paths";
import Icon from "../../../icon";

import Tooltip from "../../Tooltip";
import SharePlan from "../../SharePlan";
import User from "../../User";
import { Checkbox } from "../../Form";

import { StyledLastRow, LastModified, BasicInfo, ComposerImg, Public, StartPlay, ArchiveWrapper, ComposerRow } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";


const LastRow = ({
    plan,
    tiny,
    isBrowse,
    play,
    isHomework,
    showCreatedDate,
    handleArchivedChange,
    moderation,
    privateView,
    handleCheckPrivate,
    onComposerClick,
}: IProps) => {
    const { formatMessage, formatDate, formatTime, locale } = useIntl();
    const [isShareModalOpen, toggleShareModal] = useState(false);
    const user = useSelector((state: IStateTree) => state.user as IUser);

    const handleComposerClick = () => {
        if (plan.meta.opus.composer) {
            onComposerClick?.(plan.meta.opus.composer);
        }
    }
    return (
        <StyledLastRow tiny={!!tiny}>
            {!isBrowse && !play && !isHomework && !moderation && (
                <LastModified>
                    {showCreatedDate ? (
                        `${formatMessage(messages.created)} ${timeago.format(plan.created, locale)}`
                    ) : plan.modified ? (
                        `${formatMessage(messages.modified)} ${timeago.format(plan.modified, locale)}`
                    ) : (
                        <FormattedMessage {...messages.noData} />
                    )}
                </LastModified>
            )}
            {isHomework && (
                <LastModified>
                    {plan.date! < Date.now() ? (
                        <>
                            {!plan.isArchived && <Icon i="alert" />}
                            {formatMessage(messages.expiredTimeago, {
                                timeago: timeago.format(plan.date!, locale),
                            })}
                        </>
                    ) : (
                        <>
                            <Icon i="clock" />
                            {formatDate(plan.date, { weekday: "long" })},{" "}
                            {formatDate(plan.date, { month: "long", day: "numeric" })} {formatTime(plan.date)}
                        </>
                    )}
                </LastModified>
            )}
            {isHomework && plan.date! < Date.now() && user.loggedIn && !user.isStudent && (
                <ArchiveWrapper>
                    <Checkbox
                        label={formatMessage(messages.archived)}
                        checked={!!plan.isArchived}
                        onChange={handleArchivedChange!}
                    />
                </ArchiveWrapper>
            )}
            {(isBrowse || moderation) && !privateView && (
                <BasicInfo>
                    {plan.meta.opus.planType === "written" && (
                        <ComposerRow onClick={handleComposerClick} className={cn({ Clickable: !!plan.meta.opus.composer })}>
                            {plan.meta.opus.composerImg && (
                                <ComposerImg src={host + "composers/" + plan.meta.opus.composerImg}></ComposerImg>
                            )}
                            <div>
                                {!!plan.meta.opus.composerName ? formatComposerName(plan.meta.opus.composerName, locale) : <FormattedMessage {...messages.unknownComposer} />}
                            </div>
                        </ComposerRow>
                    )}
                    {plan.meta.opus.planType === "folk" && (
                        <>
                            <div>
                                {plan.meta.opus.isChildrenSong
                                    ? formatMessage(messages.isChildrenSong)
                                    : plan.meta.opus.isChurchMusic
                                    ? formatMessage(messages.isChurchMusic)
                                    : getSelectValue(folksongTempos(formatMessage), plan.meta.opus.folksongTempo)}
                            </div>
                        </>
                    )}
                    {plan.meta.opus.planType === "exercise" && plan._id !== christmasId && (
                        <>
                            <div>{getSelectValue(exerciseTypes(formatMessage), plan.meta.opus.exerciseType)}</div>
                        </>
                    )}
                    {plan._id === christmasId && (
                        <div>
                            <FormattedMessage {...messages.christmasEdition} />
                        </div>
                    )}
                </BasicInfo>
            )}
            {(isBrowse || moderation) && <User user={plan.userData!} />}
            {!isBrowse && !play && !isHomework && !moderation && (
                <Public>
                    <Tooltip label={formatMessage(messages.share)} disabled={!plan.shared}>
                        <PublishButton
                            disabled={!plan.shared}
                            className={cn(plan.shared && "Active")}
                            onClick={() => toggleShareModal(true)}
                        >
                            <Icon i="share" />
                        </PublishButton>
                    </Tooltip>
                </Public>
            )}
            {privateView && (
                <ArchiveWrapper>
                    <Checkbox label={formatMessage(messages.archived)} onChange={() => handleCheckPrivate!(plan)} checked={false} />
                </ArchiveWrapper>
            )}
            {isShareModalOpen && <SharePlan close={() => toggleShareModal(false)} plan={plan} />}
            {play && (
                <StartPlay onClick={play}>
                    <FormattedMessage {...messages.start} />
                </StartPlay>
            )}
        </StyledLastRow>
    );
};

export default LastRow;
